/**
 * Shared chunk boundary marker insertion logic.
 *
 * Browser-safe — no Node-only dependencies. Used by both the web client
 * (ChunkAnalysisView, with emoji markers) and the export pipeline
 * (chunk-visualization-renderer, with HTML span markers).
 *
 * The core algorithm — position-based splice into raw markdown — lives here
 * once to avoid duplication.
 */

// ===== Shared types =====

export interface StructuralDesert {
  tokenStart: number;
  tokenEnd: number;
  charStart: number;
  charEnd: number;
  lengthTokens: number;
  lengthChars: number;
  precedingMarker: string | null;
  followingMarker: string | null;
}

export interface WindowAlignment {
  windowIndex: number;
  tokenStart: number;
  charStart: number;
  aligned: boolean;
}

export interface BestChunk {
  tokenStart: number;
  tokenEnd: number;
  charStart: number;
  charEnd: number;
  decodedText: string;
  relevanceScore: number;
  selfContainedStatus: "self-contained" | "partial" | "fragmented";
  conditionsPassed: string[];
}

/** Marker strings to splice into the raw markdown. */
export interface MarkerSet {
  bestStart: string;
  bestEnd: string;
  desertStart: string;
  desertEnd: string;
  windowAligned: string;
  windowUnaligned: string;
}

// ===== Default marker sets =====

/** Emoji markers for the interactive web client. */
export const EMOJI_MARKERS: MarkerSet = {
  bestStart: "\u{1F3C5}\u23F5",
  bestEnd: "\u23F4\u{1F3C5}",
  desertStart: "\u{1F7E0}\u23F5",
  desertEnd: "\u23F4\u{1F7E0}",
  windowAligned: "\u{1F7E2}",
  windowUnaligned: "\u{1F534}",
};

/**
 * Inline-styled HTML span markers for PDF/PPT export via Carbone.
 * These survive the remark→rehype pipeline when `rehype-raw` is enabled.
 *
 * Uses Unicode guillemets (» «) instead of angle brackets (>> <<) to avoid
 * double-encoding: rehype-raw decodes HTML entities to literal chars, then
 * rehype-stringify re-encodes < and & → &#x3C; &#x26;. Guillemets are safe
 * Unicode text that passes through both stages unchanged.
 */
export const HTML_MARKERS: MarkerSet = {
  bestStart:
    '<span style="color:#2e7d32;font-weight:bold;"><span style="font-size:1.6em;">\u00BB</span> Best Chunk</span> ',
  bestEnd:
    ' <span style="color:#2e7d32;font-weight:bold;"><span style="font-size:1.6em;">\u00AB</span></span>',
  desertStart:
    '<span style="color:#e65100;font-weight:bold;"><span style="font-size:1.6em;">\u00BB</span> Desert</span> ',
  desertEnd:
    ' <span style="color:#e65100;font-weight:bold;"><span style="font-size:1.6em;">\u00AB</span></span>',
  windowAligned:
    '<span style="color:#4caf50;font-weight:bold;">|</span>',
  windowUnaligned:
    '<span style="color:#f44336;font-weight:bold;">|</span>',
};

// ===== Core algorithm =====

/**
 * If `pos` falls at the start of a markdown structural prefix (heading, list
 * item, blockquote), return the position *after* the prefix so the marker
 * doesn't break rendering.
 */
export function adjustForStructuralPrefix(
  markdown: string,
  pos: number
): number {
  if (pos !== 0 && markdown[pos - 1] !== "\n") return pos;

  const rest = markdown.slice(pos);

  const headingMatch = rest.match(/^(#{1,6}\s)/);
  if (headingMatch) return pos + headingMatch[1].length;

  const ulMatch = rest.match(/^(\s*[-*]\s)/);
  if (ulMatch) return pos + ulMatch[1].length;

  const olMatch = rest.match(/^(\s*\d+\.\s)/);
  if (olMatch) return pos + olMatch[1].length;

  const bqMatch = rest.match(/^(>\s)/);
  if (bqMatch) return pos + bqMatch[1].length;

  return pos;
}

/**
 * Insert boundary markers into raw markdown at the character positions of
 * deserts, best chunk, and window boundaries.
 *
 * The `markers` parameter controls what strings are spliced in — emoji for
 * the web client, HTML spans for export.
 */
export function insertBoundaryMarkers(
  markdown: string,
  deserts: StructuralDesert[],
  bestChunk: BestChunk | null,
  alignments: WindowAlignment[],
  options: {
    showDeserts: boolean;
    showBestChunk: boolean;
    showWindows: boolean;
  },
  markers: MarkerSet = EMOJI_MARKERS
): string {
  type Insertion = { pos: number; marker: string };
  const insertions: Insertion[] = [];

  if (options.showDeserts) {
    for (const d of deserts) {
      insertions.push({
        pos: adjustForStructuralPrefix(markdown, d.charStart),
        marker: markers.desertStart,
      });
      insertions.push({ pos: d.charEnd, marker: markers.desertEnd });
    }
  }

  if (options.showBestChunk && bestChunk) {
    insertions.push({
      pos: adjustForStructuralPrefix(markdown, bestChunk.charStart),
      marker: markers.bestStart,
    });
    insertions.push({ pos: bestChunk.charEnd, marker: markers.bestEnd });
  }

  if (options.showWindows) {
    for (const w of alignments) {
      const skipBecauseOverlapsBestChunk =
        options.showBestChunk &&
        bestChunk &&
        w.charStart === bestChunk.charStart;

      if (skipBecauseOverlapsBestChunk) continue;

      if (w.charStart >= 0 && w.charStart <= markdown.length) {
        insertions.push({
          pos: adjustForStructuralPrefix(markdown, w.charStart),
          marker: w.aligned ? markers.windowAligned : markers.windowUnaligned,
        });
      }
    }
  }

  if (insertions.length === 0) return markdown;

  insertions.sort((a, b) => b.pos - a.pos);

  let result = markdown;
  for (const ins of insertions) {
    const pos = Math.min(ins.pos, result.length);
    result = result.slice(0, pos) + ins.marker + result.slice(pos);
  }

  return result;
}
